import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLang } from "../contexts/LanguageContext";
import { Star, Send, Loader2, MessageSquare } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StarRating({ value, onChange, readonly = false, size = 18 }) {
  return (
    <div className="flex gap-1">
      {[1,2,3,4,5].map((star) => (
        <button key={star} type="button" disabled={readonly} onClick={() => onChange && onChange(star)} className={`transition-all ${readonly?"cursor-default":"cursor-pointer hover:scale-110 active:scale-95"}`}>
          <Star size={size} className={star <= value ? "text-amber-400 fill-amber-400" : "text-slate-500"} strokeWidth={1.5} />
        </button>
      ))}
    </div>
  );
}

export default function ReviewsSection() {
  const { isLoggedIn, api } = useAuth();
  const { theme } = useTheme();
  const { t } = useLang();
  const isDark = theme === "dark";
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const textPrimary = isDark ? "text-white" : "text-slate-900";
  const textMuted = isDark ? "text-slate-400" : "text-slate-500";
  const labelColor = isDark ? "text-amber-400" : "text-amber-600";
  const inputBg = isDark ? "bg-slate-950/40 border-white/10 text-white placeholder:text-slate-500" : "bg-white/50 border-slate-200 text-slate-900 placeholder:text-slate-400";

  useEffect(() => { axios.get(`${API}/reviews`).then((r) => { setReviews(r.data.reviews||[]); setAvgRating(r.data.average_rating||0); setTotal(r.data.total||0); }).catch(() => {}); }, [submitted]);

  const handleSubmit = async () => {
    if (!text.trim() || rating < 1) return; setSubmitting(true);
    try { await api().post("/reviews", { rating, text }); setSubmitted(true); setShowForm(false); setText(""); } catch (e) { console.error(e); } finally { setSubmitting(false); }
  };

  const formatDate = (iso) => new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="mt-8 animate-fadeInUp" style={{ animationDelay: "0.35s" }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className={`text-[10px] ${textMuted} uppercase tracking-[0.2em] font-semibold`}>{t("userReviews")}</p>
          {total > 0 && <div className="flex items-center gap-2 mt-1"><StarRating value={Math.round(avgRating)} readonly size={14} /><span className={`text-xs font-semibold ${textPrimary}`}>{avgRating}</span><span className={`text-[10px] ${textMuted}`}>({total} {t("reviews")})</span></div>}
        </div>
        {isLoggedIn && !submitted && (
          <button data-testid="write-review-button" onClick={() => setShowForm(!showForm)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all ${isDark?"bg-amber-500/20 text-amber-400":"bg-amber-500/15 text-amber-600"}`}>
            <MessageSquare size={12} /> {t("writeReview")}
          </button>
        )}
      </div>
      {showForm && (
        <div data-testid="review-form" className="glass-card p-4 mb-4 animate-fadeIn">
          <p className={`text-[10px] ${labelColor} uppercase tracking-[0.15em] font-semibold mb-3`}>{t("yourReviewMatters")}</p>
          <div className="mb-3"><StarRating value={rating} onChange={setRating} size={24} /></div>
          <textarea data-testid="review-text-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("shareExperience")} className={`w-full border rounded-xl p-3 min-h-[80px] focus:border-amber-500/50 outline-none transition-all resize-none text-sm ${inputBg}`} />
          <button data-testid="submit-review-button" onClick={handleSubmit} disabled={submitting || !text.trim()} className="mt-2 w-full btn-gold py-2.5 text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {submitting ? t("sending") : t("publishReview")}
          </button>
        </div>
      )}
      {total === 0 ? (
        <div className="glass-card p-5 text-center"><MessageSquare size={24} className={`${textMuted} mx-auto mb-2`} /><p className={`text-sm ${textMuted}`}>{t("noReviews")}</p><p className={`text-xs ${textMuted} mt-1`}>{t("beFirst")}</p></div>
      ) : (
        <div className="space-y-2.5">
          {reviews.slice(0, 5).map((review, i) => (
            <div key={review.id || i} data-testid={`review-card-${i}`} className="glass-card p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${isDark?"bg-amber-500/20 text-amber-400":"bg-amber-500/15 text-amber-600"}`}>{(review.author_name||"A")[0].toUpperCase()}</div>
                  <div><p className={`text-sm font-semibold ${textPrimary}`}>{review.author_name}</p><p className={`text-[10px] ${textMuted}`}>{formatDate(review.created_at)}</p></div>
                </div>
                <StarRating value={review.rating} readonly size={12} />
              </div>
              <p className={`text-sm ${isDark?"text-slate-300":"text-slate-600"} leading-relaxed`}>{review.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
